import jwt_decode, { JwtPayload } from "jwt-decode";
import { User, UserManager } from "oidc-client-ts";
import { SignInRedirectParams } from "piral-oidc";
import OidcError from "./OidcError";
import { AuthenticationResult, OidcClient, OidcConfig, OidcErrorType, OidcProfile } from "./types";

let userCache: User = undefined;

function doesWindowLocationMatch(targetUri: string) {
    return globalThis.location.pathname === new URL(targetUri).pathname;
}

function retrieveToken(userManager: UserManager): Promise<string> {
    return new Promise<string>((res, rej) => {
        userManager
            .getUser()
            .then((user) => {
                if (!user) {
                    rej(new OidcError(OidcErrorType.notAuthorized));
                } else if (user.access_token && user.expires_in > 300) {
                    userManager.storeUser(user);
                    res(user.access_token);
                } else {
                    userCache = user;
                    userManager.storeUser(user);
                    const refreshToken = user.refresh_token;
                    if (refreshToken && jwt_decode<JwtPayload>(refreshToken).exp < Date.now() / 1000) {
                        return rej(new OidcError(OidcErrorType.silentRenewFailed));
                    }
                    return userManager.signinSilent().then((user) => {
                        if (!user) {
                            return rej(new OidcError(OidcErrorType.silentRenewFailed));
                        }
                        if (!user.access_token) {
                            return rej(new OidcError(OidcErrorType.invalidToken));
                        }

                        return res(user.access_token);
                    });
                }
            })
            .catch((err) => rej(new OidcError(OidcErrorType.unknown, err)));
    });
}
function signInSilent(userManager: UserManager): Promise<string> {
    return new Promise<string>((res, rej) => {
        return userManager
            .signinSilent()
            .then((user) => {
                if (!user) {
                    return rej(new OidcError(OidcErrorType.silentRenewFailed));
                }
                if (!user.access_token) {
                    return rej(new OidcError(OidcErrorType.invalidToken));
                }
                userManager.storeUser(user);
                userCache = user;
                return res(user.access_token);
            })
            .catch((err) => rej(new OidcError(OidcErrorType.unknown, err)));
    });
}

function retrieveRefreshToken(userManager: UserManager): Promise<string> {
    return new Promise<string>((res, rej) => {
        userManager
            .getUser()
            .then((user) => {
                if (user) {
                    userCache = user;
                    userManager.storeUser(user);
                    return res(user.refresh_token);
                }

                return rej(new OidcError(OidcErrorType.notAuthorized));
            })
            .catch((err) => rej(new OidcError(OidcErrorType.unknown, err)));
    });
}

function retrieveIdToken(userManager: UserManager): Promise<string> {
    return new Promise<string>((res, rej) => {
        userManager
            .getUser()
            .then((user) => {
                if (user?.id_token) {
                    // As userManager object does not expose expiration time of an ID token_
                    // this information is being extracted from the token itself.
                    const currentTime = Math.floor(Date.now() / 1000);
                    const idTokenExpiresAt = Number(JSON.parse(atob(user.id_token.split(".")[1])).exp);
                    const idTokenExpiresIn = idTokenExpiresAt - currentTime;
                    if (idTokenExpiresIn > 300) {
                        // Sign in if token expires within 300 seconds
                        return res(user.id_token);
                    }

                    userCache = user;
                    userManager.storeUser(user);
                    const refreshToken = user.refresh_token;
                    if (refreshToken && jwt_decode<JwtPayload>(refreshToken).exp < Date.now() / 1000) {
                        return rej(new OidcError(OidcErrorType.silentRenewFailed));
                    }
                    return userManager
                        .signinSilent()
                        .then((user) => {
                            if (!user) {
                                return rej(new OidcError(OidcErrorType.silentRenewFailed));
                            }
                            if (!user.id_token) {
                                return rej(new OidcError(OidcErrorType.invalidToken));
                            }
                            userCache = user;
                            userManager.storeUser(user);
                            return res(user.id_token);
                        })
                        .catch((err) => {
                            rej(new OidcError(OidcErrorType.unknown, err));
                        });
                }

                if (user) {
                    return rej(new OidcError(OidcErrorType.invalidToken));
                }

                return rej(new OidcError(OidcErrorType.notAuthorized));
            })
            .catch((err) => {
                rej(new OidcError(OidcErrorType.unknown, err));
            });
    });
}

function retrieveProfile(userManager: UserManager): Promise<OidcProfile> {
    return new Promise<OidcProfile>((res, rej) => {
        userManager.getUser().then(
            (user) => {
                if (!user || user.expires_in <= 0) {
                    return rej(new OidcError(OidcErrorType.notAuthorized));
                }
                userCache = user;
                userManager.storeUser(user);
                return res(user.profile);
            },
            (err) => rej(new OidcError(OidcErrorType.unknown, err)),
        );
    });
}

function handleAuthentication(
    appUri: string,
    userManager: UserManager,
    signInRedirectParams: SignInRedirectParams,
    extraQueryParams: Record<string, string> = {},
    parentName?: string,
): Promise<AuthenticationResult> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
        /** The user that is resolved when finishing the callback  */
        let user: User;
        if (
            (doesWindowLocationMatch(userManager.settings.silent_redirect_uri) ||
                doesWindowLocationMatch(userManager.settings.popup_redirect_uri)) &&
            !isMainWindow(parentName)
        ) {
            /*
             * This is a silent redirect frame. The correct behavior is to notify the parent of the updated user,
             * and then to do nothing else. Encountering an error here means the background IFrame failed
             * to update the parent. This is usually due to a timeout from a network error.
             */
            try {
                user = await userManager.signinSilentCallback();
                userCache = user;
                userManager.storeUser(user);
            } catch (e) {
                return reject(new OidcError(OidcErrorType.oidcCallback, e));
            }
            return resolve({
                shouldRender: false,
                state: user?.state,
            });
        }

        if (doesWindowLocationMatch(userManager.settings.redirect_uri) && isMainWindow(parentName)) {
            try {
                user = await userManager.signinCallback();
                userCache = user;
                userManager.storeUser(user);
            } catch (e) {
                /*
                 * Failing to handle a sign-in callback is non-recoverable. The user is expected to call `logout()`, after
                 * logging this error to their internal error-handling service. Usually, this is due to a misconfigured auth server.
                 */
                return reject(new OidcError(OidcErrorType.oidcCallback, e));
            }

            if (appUri) {
                console.debug(`Redirecting to ${appUri} due to appUri being configured.`);
                globalThis.location.href = appUri;
                return resolve({
                    shouldRender: false,
                    state: user?.state,
                });
            }

            /* If appUri is not configured, we let the user decide what to do after getting a session. */
            return resolve({
                shouldRender: true,
                state: user?.state,
            });
        }

        /*
         * The current page is a normal flow, not a callback or signout. We should retrieve the current access_token,
         * or log the user in if there is no current session.
         * This branch of code should also tell the user to render the main application.
         */
        return retrieveToken(userManager)
            .then((token) => {
                if (token) {
                    return resolve({ shouldRender: true });
                }
                /* We should never get into this state, retrieveToken() should reject if there is no token */
                return reject(new OidcError(OidcErrorType.invalidToken));
            })
            .catch(async (reason: OidcError) => {
                if (reason.type === OidcErrorType.notAuthorized) {
                    /*
                     * Expected Error during normal code flow:
                     * This is the first time logging in since a logout (or ever), instead of asking the user
                     * to call `login()`, just perform it ourself here.
                     *
                     * The resolve shouldn't matter, as `signinRedirect` will redirect the browser location
                     * to the user's configured redirectUri.
                     */
                    await userManager.signinRedirect({
                        ...signInRedirectParams,
                        extraQueryParams: extraQueryParams,
                    });
                    return resolve({ shouldRender: false });
                }

                /*
                 * Getting here is a non-recoverable error. It is up to the user to determine what to do.
                 * Usually this is a result of failing to reach the authentication server, or a misconfigured
                 * authentication server, or a bad clock skew (commonly caused by docker in windows).
                 */
                return reject(reason);
            });
    });
}

function isMainWindow(parentName?: string) {
    return parentName ? parentName === globalThis.parent?.name : globalThis.window === globalThis.top;
}

/**
 * Sets up a new client wrapping the oidc-client API.
 * @param config The configuration for the client.
 */
export function setupOidcClient(config: OidcConfig): OidcClient {
    const {
        clientId,
        identityProviderUri,
        redirectUri = `${globalThis.location.origin}/auth`,
        signInRedirectParams,
        postLogoutRedirectUri = globalThis.location.origin,
        responseType,
        scopes,
        restrict = false,
        parentName,
        appUri,
        logLevel,
        userStore,
    } = config;

    const userManager = new UserManager({
        authority: identityProviderUri,
        redirect_uri: redirectUri,
        silent_redirect_uri: redirectUri,
        popup_redirect_uri: redirectUri,
        post_logout_redirect_uri: postLogoutRedirectUri,
        client_id: clientId,
        response_type: responseType,
        scope: scopes?.join(" "),
        extraQueryParams: config.extraQueryParams,
        userStore,
    });

    if (doesWindowLocationMatch(userManager.settings.post_logout_redirect_uri)) {
        if (isMainWindow(parentName)) {
            userManager.signoutRedirectCallback();
        } else {
            userManager.signoutPopupCallback();
        }
    }

    return {
        login() {
            return userManager.signinRedirect({ ...signInRedirectParams, extraQueryParams: config.extraQueryParams });
        },
        logout() {
            sessionStorage.clear();
            userManager.clearStaleState();
            userManager.removeUser();
            // @ts-ignore
            if (userStore.clear) {
                // @ts-ignore
                userStore.clear();
            }
            return userManager.signoutRedirect({
                extraQueryParams: {
                    id_token_hint: userCache?.id_token,
                },
            });
        },
        handleAuthentication() {
            return handleAuthentication(appUri, userManager, signInRedirectParams, config.extraQueryParams, parentName);
        },
        extendHeaders(req) {
            if (!restrict) {
                req.setHeaders(
                    retrieveIdToken(userManager).then(
                        (token) => token && { Authorization: `Bearer ${token}` },
                        () => {
                            this.logout();
                        },
                    ),
                );
            }
        },
        graphToken() {
            return retrieveToken(userManager);
        },
        token() {
            return Promise.resolve(
                retrieveIdToken(userManager).then(
                    (token) => {
                        return token;
                    },
                    () => {
                        this.logout();
                        return "";
                    },
                ),
            );
        },
        account() {
            return retrieveProfile(userManager);
        },
        getRefreshToken() {
            return retrieveRefreshToken(userManager);
        },
        signInSilent() {
            return signInSilent(userManager);
        },
    };
}
